<template>
  <main class="row pt-3 mx-0">
    <b-col
      lg="10"
      offset-lg="1"
    >
      <h1 class="mb-4">
        {{ $t('Export documents for a period') }}
      </h1>
      <b-row>
        <b-col lg="4">
          <dx-form-group :label="$t('company')">
            <dx-select-box
              v-model="form.company"
              :data-source="companies"
              value-expr="id"
              :display-expr="companyDisplayExpr"
              styling-mode="outlined"
            />
          </dx-form-group>
        </b-col>
        <b-col lg="4">
          <dx-form-group :label="$t('from date')">
            <dx-date-box
              v-model="form.fromDate"
              date-serialization-format="yyyy-MM-dd"
              type="date"
              class="w-100"
              styling-mode="outlined"
            />
          </dx-form-group>
        </b-col>
        <b-col lg="4">
          <dx-form-group :label="$t('to date')">
            <dx-date-box
              v-model="form.toDate"
              date-serialization-format="yyyy-MM-dd"
              type="date"
              class="w-100"
              styling-mode="outlined"
            />
          </dx-form-group>
        </b-col>
      </b-row>
      <div class="d-flex flex-row align-items-baseline mb-4">
        <dx-button
          :text="$t('Run')"
          type="success"
          styling-mode="contained"
          class="custom-btn mr-2"
          :disabled="loading || formFieldsAreEmpty"
          @click="addExportTask"
        />
      </div>
      <dx-data-grid
        ref="exportTasksGrid"
        :data-source="exportTasks"
        @toolbar-preparing="onToolbarPreparing"
      >
        <dx-column
          data-field="company"
          :caption="$t('company')"
          :lookup="companyLookup"
        />
        <dx-column
          data-field="from_date"
          :caption="$t('from date')"
          data-type="date"
        />
        <dx-column
          data-field="to_date"
          :caption="$t('to date')"
          data-type="date"
        />
        <dx-column
          data-field="celery_task_status"
          :caption="$t('status')"
          cell-template="status-template"
        />
        <dx-column
          data-field="done"
          :caption="$t('download')"
          cell-template="file-cell-template"
        />
        <template #status-template="{ data }">
          {{ $t(`export_tasks.statuses.${data.data.celery_task_status}`) }}
        </template>
        <template #file-cell-template="{ data }">
          <a
            v-if="data.data.done"
            href="#"
            @click.prevent="openFile(data)"
          >
            <i class="ace-icon icon-download" />
          </a>
        </template>
      </dx-data-grid>
    </b-col>
  </main>
</template>

<script>
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid';
import { DxButton, DxSelectBox, DxDateBox, DxCheckBox } from 'devextreme-vue'; // eslint-disable-line

import { mapState } from 'vuex';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale as localize, loadMessages } from 'devextreme/localization';

import DxFormGroup from '@/components/common/DxFormGroup';

import createCustomStore from '@/utils/create-custom-store';

const contentDisposition = require('content-disposition');

export default {
  name: 'ExportTasks',
  components: {
    DxButton,
    DxSelectBox,
    DxDateBox,
    DxFormGroup,
    DxDataGrid,
    DxColumn,
  },
  data() {
    return {
      loading: false,
      form: {
        company: null,
        fromDate: null,
        toDate: null,
      },
      exportTasks: createCustomStore('/tracking/api/export-tasks/'),
      companies: {
        store: createCustomStore('profile/api/companies/'),
        paginate: true,
      },
    };
  },
  computed: {
    ...mapState(['user']),
    companyLookup: () => ({
      dataSource: {
        store: createCustomStore('profile/api/companies/'),
        key: 'id',
      },
      valueExpr: 'id',
      displayExpr(company) {
        return company ? `${company.name} ${company.inn || ''}` : '';
      },
      allowClearing: true,
    }),
    formFieldsAreEmpty() {
      return Object.values(this.form).includes(null);
    },
  },
  async created() {
    loadMessages(ruMessages);
    localize(this.$i18n.locale);
  },
  methods: {
    onToolbarPreparing(e) {
      e.toolbarOptions.items.unshift({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-refresh',
          onClick: this.refreshDataGrid.bind(this),
        },
      }, {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-close',
          hint: this.$t('clear_filters'),
          onClick: this.clearFilter.bind(this),
        },
      });
    },
    refreshDataGrid() {
      this.$refs.exportTasksGrid.instance.refresh();
    },
    clearFilter() {
      this.$refs.exportTasksGrid.instance.clearFilter();
    },
    async addExportTask() {
      this.loading = true;
      await this.$http
        .post('/tracking/api/export-tasks/', {
          company: this.form.company,
          from_date: this.form.fromDate,
          to_date: this.form.toDate,
        })
        .finally(() => {
          this.loading = false;
        });

      Object.assign(this.form, this.$options.data().form);
      this.$refs.exportTasksGrid.instance.refresh();
    },
    companyDisplayExpr(company) {
      return company ? `${company.name} ${company.inn || ''}` : '';
    },
    openFile({ data }) {
      this.$http({
        url: process.env.VUE_APP_API_URL + `/tracking/api/export-tasks/${data.id}`,
        method: 'GET',
        responseType: 'blob',
      }).then(response => {
        const href = window.URL.createObjectURL(new Blob([response.data]));
        const disposition = contentDisposition.parse(response.headers['content-disposition']);

        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', disposition.parameters.filename);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
  },
};
</script>

<style lang="scss">
.dx-field-label--fit {
  width: fit-content !important;
}
</style>
